/**
 * @Author: v_jppjpeng
 * @Date: 2023-07-28 10:53:38
 * @Explain: 链接按钮
 */
import { Icon, Tooltip } from '@/components/tdesign';
import classNames from 'classnames';
import { ReactNode, isValidElement, useMemo } from 'react';
import {
  LinkProps,
  Loading,
  PopupProps,
  Link as TLink,
  TooltipProps,
} from 'tdesign-react';
import { IconProps } from '../data.d';

type PropsType = {
  children?: React.ReactNode;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  style?: LinkProps['style'];
  theme?: LinkProps['theme'];
  size?: LinkProps['size'];
  hover?: LinkProps['hover'];
  /** 前置图标 */
  prefixIcon?: LinkProps['prefixIcon'];
  prefixIconName?: string;
  /** 后置图标 */
  suffixIcon?: LinkProps['suffixIcon'];
  suffixIconName?: string;
  /** 跳转到 */
  to?: string;
  /** 提示语 */
  tooltip?: React.ReactNode | string;
  /** 浮层内容部分样式，第一个参数 triggerElement 表示触发元素 DOM 节点，第二个参数 popupElement 表示浮层元素 DOM 节点 */
  overlayInnerStyle?: PopupProps['overlayInnerStyle'];
  linkProps?: LinkProps;
  tipProps?: TooltipProps & { placementOffset?: any[] };
  iconProps?: IconProps;
  onClick?: LinkProps['onClick'];
};

function Link(props: PropsType) {
  const {
    children,
    className,
    style,
    loading,
    disabled,
    theme = 'primary',
    size = 'large',
    hover = 'color',
    prefixIcon,
    prefixIconName,
    suffixIcon,
    suffixIconName,
    overlayInnerStyle,
    to,
    linkProps,
    tooltip,
    tipProps,
    iconProps,
    onClick,
  } = props;

  const iconRender: any = (iconName?: string, iconEl?: ReactNode) => {
    if (iconName) {
      return <Icon.Font name={iconName} {...iconProps} />;
    } else if (iconEl) {
      return iconEl;
    } else {
      return undefined;
    }
  };

  const customProps: LinkProps = useMemo(
    () => ({
      style,
      disabled,
      loading,
      theme,
      size,
      hover,
      prefixIcon:
        prefixIcon || prefixIconName
          ? iconRender(prefixIconName, prefixIcon)
          : undefined,
      suffixIcon:
        suffixIcon || suffixIconName
          ? iconRender(suffixIconName, suffixIcon)
          : undefined,
      to,
      onClick,
    }),
    [
      style,
      disabled,
      theme,
      size,
      hover,
      prefixIcon,
      prefixIconName,
      suffixIcon,
      suffixIconName,
      to,
      onClick,
    ],
  );

  const onRender = () => {
    return loading ? (
      <Loading
        loading={true}
        text={
          <TLink
            className={classNames('default-td-link', className)}
            {...customProps}
            {...linkProps}
            disabled
          >
            {children}
          </TLink>
        }
        size="small"
      />
    ) : (
      <TLink
        className={classNames('default-td-link', className)}
        {...customProps}
        {...linkProps}
      >
        {children}
      </TLink>
    );
  };

  return tooltip ? (
    <Tooltip
      content={
        isValidElement(tooltip) ? (
          tooltip
        ) : (
          <span className="custom-content">{tooltip}</span>
        )
      }
      overlayInnerStyle={overlayInnerStyle}
      showArrow
      destroyOnClose
      {...tipProps}
    >
      {onRender()}
    </Tooltip>
  ) : (
    onRender()
  );
}

export default Link;
